/**
 * IndividualPicker — multi-add contact picker for SuggestResourceForm
 * org-mode "Contact person(s)" field.
 *
 * Phase 1.7.4 evolution: was single-select (selectedId XOR inlineDraft);
 * now multi-add (entries[] of mixed kind). The search input + "+ Add new
 * contact" button persist after each add so the steward can stack
 * multiple contacts in one submit. Each entry renders as its own
 * chip (existing) or summary card (inline, with Edit details + ×).
 *
 * Entry shape:
 *   { kind: 'existing', id: string }
 *   { kind: 'inline',   draft: IndividualPayload }
 *
 * The parent maps entries[] → suggested_resources.payload.contacts[]
 * (server-side back-compat handles the legacy singular fields per
 * api/_shared/repos/suggestedResources.ts).
 *
 * Phase 1.7.4 label rename: button reads "+ Add new contact" (was
 * "+ Add new individual") to align with the rendered card header
 * "NEW CONTACT (WILL BE CREATED ON APPROVE)" and the SuggestResourceForm
 * field label "Contact person (existing or new)". Component name
 * (IndividualPicker) is unchanged — it's still individual-entity-typed;
 * "contact" is the contextual semantic in this form.
 *
 * UX-13 S5 — TypeScript; the add/save/cancel actions compose <Button>
 * (the retired rounded-full + text-white pills go with it). The ✕ remove
 * glyphs + option rows + the Edit-details text link stay native buttons
 * (not Button-class affordances), so the file stays consciously
 * allowlisted.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useUnifiedSearch } from '@/hooks/useUnifiedSearch';
import { Button } from '@/components/primitives/Button';
import { SearchField } from '@/components/primitives/SearchField';
import { TextInput } from '@/components/primitives/TextInput';
import type { IndividualSubmitPayload } from '@/lib/suggestedResources';
import type { IndividualForSearch } from '@/lib/types';
import { AddressCheckChoices, AddressCheckPrompt, AddressFields, useAddressForm, type AddressFormParts } from '@/components/clientRecord/AddressesCard';
import { postalPartsOf } from '@/lib/address';

export type ContactEntry =
  | { kind: 'existing'; id: string }
  | { kind: 'inline'; draft: IndividualSubmitPayload };

export interface IndividualPickerProps {
  entries: ContactEntry[];
  onChange: (entries: ContactEntry[]) => void;
}

export function IndividualPicker({ entries, onChange }: IndividualPickerProps): ReactNode {
  // useUnifiedSearch is still plain JS; type the boundary once here.
  const { contacts: contactsUntyped } = useUnifiedSearch();
  const contacts: IndividualForSearch[] = contactsUntyped;
  const [searchTerm, setSearchTerm] = useState('');
  const [isInlineOpen, setIsInlineOpen] = useState(false);
  // When editing an existing inline entry (index into entries[]) — null
  // means the inline sub-form is opened in "add new" mode.
  const [editingIdx, setEditingIdx] = useState<number | null>(null);

  const idToContact = useMemo(() => {
    const m = new Map<string, IndividualForSearch>();
    for (const c of contacts) m.set(c.id, c);
    return m;
  }, [contacts]);

  // Already-added existing ids — suppress them from search results so the
  // steward can't double-add the same existing contact.
  const addedExistingIds = useMemo(() => {
    const s = new Set<string>();
    for (const e of entries) {
      if (e.kind === 'existing') s.add(e.id);
    }
    return s;
  }, [entries]);

  const searchResults = useMemo(() => {
    if (!searchTerm.trim()) return [];
    const term = searchTerm.trim().toLowerCase();
    return contacts
      .filter((c) => !addedExistingIds.has(c.id))
      .filter((c) => c.full_name.toLowerCase().includes(term))
      .slice(0, 8);
  }, [searchTerm, contacts, addedExistingIds]);

  function addExisting(id: string) {
    onChange([...entries, { kind: 'existing', id }]);
    setSearchTerm('');
  }

  function addInline(draft: IndividualSubmitPayload) {
    onChange([...entries, { kind: 'inline', draft }]);
  }

  function updateInlineAt(idx: number, draft: IndividualSubmitPayload) {
    const next = entries.slice();
    next[idx] = { kind: 'inline', draft };
    onChange(next);
  }

  function removeAt(idx: number) {
    onChange(entries.filter((_, i) => i !== idx));
  }

  return (
    <div>
      {/* Stacked entries — chips for existing, cards for inline. */}
      {entries.length > 0 && (
        <div className="mb-2 space-y-2">
          {entries.map((entry, idx) => {
            if (entry.kind === 'existing') {
              const c = idToContact.get(entry.id);
              return (
                <ExistingContactChip
                  key={`existing-${entry.id}`}
                  contact={c}
                  onRemove={() => removeAt(idx)}
                />
              );
            }
            return (
              <InlineContactCard
                key={`inline-${idx}`}
                draft={entry.draft}
                onEdit={() => {
                  setEditingIdx(idx);
                  setIsInlineOpen(true);
                }}
                onRemove={() => removeAt(idx)}
              />
            );
          })}
        </div>
      )}

      {/* Persistent search input. */}
      <SearchField
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Find a contact"
        aria-label="Find a contact"
        testId="individual-picker-search"
      />
      {searchTerm && searchResults.length > 0 && (
        <ul className="mt-1 max-h-48 overflow-y-auto bg-white border border-sage-800/[8%] rounded-md text-sm">
          {searchResults.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => addExisting(c.id)}
                className="block w-full text-left px-3 py-2 hover:bg-sage-50 cursor-pointer"
              >
                <div className="font-semibold text-ink">{c.full_name}</div>
                {c.title && (
                  <div className="text-xs text-ink-soft">{c.title}</div>
                )}
                {Array.isArray(c.current_org_names) &&
                  c.current_org_names.length > 0 && (
                    <div className="text-xs text-ink-soft mt-0.5">
                      {c.current_org_names[0]}
                      {c.current_org_names.length > 1 &&
                        ` +${c.current_org_names.length - 1} more`}
                    </div>
                  )}
              </button>
            </li>
          ))}
        </ul>
      )}
      {searchTerm && searchResults.length === 0 && (
        <p className="mt-1 text-xs text-ink-soft">Add a new contact below.</p>
      )}

      {/* Persistent "+ Add new contact" button — stays visible after each
          add so multiple inline contacts can be stacked. */}
      <Button
        variant="secondary"
        size="sm"
        className="mt-2"
        icon={<span aria-hidden="true">+</span>}
        onClick={() => {
          setEditingIdx(null);
          setIsInlineOpen(true);
        }}
      >
        Add new contact
      </Button>

      {isInlineOpen && (
        <InlineIndividualForm
          initial={(() => {
            // editingIdx is only ever set from an inline card; narrow the union.
            const e = editingIdx !== null ? entries[editingIdx] : null;
            return e && e.kind === 'inline' ? e.draft : null;
          })()}
          onSave={(draft) => {
            if (editingIdx !== null) {
              updateInlineAt(editingIdx, draft);
            } else {
              addInline(draft);
            }
            setIsInlineOpen(false);
            setEditingIdx(null);
            setSearchTerm('');
          }}
          onCancel={() => {
            setIsInlineOpen(false);
            setEditingIdx(null);
          }}
        />
      )}
    </div>
  );
}

function ExistingContactChip({ contact, onRemove }: { contact: IndividualForSearch | undefined; onRemove: () => void }): ReactNode {
  return (
    <div className="flex items-center gap-2">
      <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-sage-100 text-sage-800 text-sm">
        <span className="font-semibold">
          {contact?.full_name ?? '(unknown contact)'}
        </span>
        {contact?.title && <span className="text-sage-600">· {contact.title}</span>}
      </span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${contact?.full_name ?? 'contact'}`}
        className="text-ink-soft hover:text-sage-800 text-sm"
      >
        ✕
      </button>
    </div>
  );
}

function InlineContactCard({ draft, onEdit, onRemove }: { draft: IndividualSubmitPayload; onEdit: () => void; onRemove: () => void }): ReactNode {
  return (
    <div className="border border-sage-200 bg-sage-50 rounded-md p-3">
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-sage-700 mb-0.5">
            New contact (will be created on approve)
          </p>
          <p className="text-sm font-semibold text-ink truncate">
            {draft.full_name}
          </p>
          {draft.title && (
            <p className="text-xs text-ink-soft mt-0.5">{draft.title}</p>
          )}
          {(draft.business_phone || draft.email) && (
            <p className="text-xs text-ink-soft mt-0.5">
              {draft.business_phone}
              {draft.business_phone && draft.email && ' · '}
              {draft.email}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove new contact ${draft.full_name}`}
          className="text-ink-soft hover:text-sage-800 text-sm shrink-0"
        >
          ✕
        </button>
      </div>
      <div className="flex gap-2 mt-2">
        <button
          type="button"
          onClick={onEdit}
          className="text-xs text-sage-700 hover:text-sage-900 underline"
        >
          Edit details
        </button>
      </div>
    </div>
  );
}

/**
 * Inline-create sub-form for a contact. Scoped to non-affiliation fields —
 * each new contact is implicitly affiliated with the parent org being
 * created (role='Contact'); other affiliations live on the individual
 * detail page (post-approve edit).
 *
 */
function InlineIndividualForm({ initial, onSave, onCancel }: {
  initial: IndividualSubmitPayload | null;
  onSave: (draft: IndividualSubmitPayload) => void;
  onCancel: () => void;
}): ReactNode {
  const i: Partial<IndividualSubmitPayload> = initial ?? {};
  const [fullName, setFullName] = useState(i.full_name ?? '');
  const [title, setTitle] = useState(i.title ?? '');
  const [email, setEmail] = useState(i.email ?? '');
  const [businessPhone, setBusinessPhone] = useState(i.business_phone ?? '');
  const [cellPhone, setCellPhone] = useState(i.cell_phone ?? '');
  const [fax, setFax] = useState(i.fax ?? '');
  // The address: the shared address form, a Directory address (a town
  // and a state at least); a street is checked before the save.
  const address = useAddressForm({
    existing: postalPartsOf(i.address),
    rule: 'directory',
    idPrefix: 'inline-contact',
    done: false,
    saving: false,
    onSave: (parts) => finishSave(parts),
  });
  const [referralPartner, setReferralPartner] = useState(
    i.is_referral_partner === true,
  );
  const [notes, setNotes] = useState(i.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  function handleSave() {
    if (fullName.trim().length === 0) {
      setError('Enter their full name.');
      return;
    }
    setError(null);
    if (!address.isEmpty()) {
      address.pressSave();
      return;
    }
    finishSave(null);
  }

  function finishSave(addressParts: AddressFormParts | null) {
    onSave({
      full_name: fullName.trim(),
      title: nullableTrim(title),
      email: nullableTrim(email),
      business_phone: nullableTrim(businessPhone),
      cell_phone: nullableTrim(cellPhone),
      fax: nullableTrim(fax),
      address: addressParts,
      is_referral_partner: referralPartner,
      notes: nullableTrim(notes),
    });
  }

  return (
    <div className="mt-3 p-3 border border-sage-800/[8%] bg-white rounded-md">
      <p className="text-xs font-semibold uppercase tracking-wider text-ink-soft mb-2">
        New contact details
      </p>
      {error && (
        <div role="alert" className="mb-2 p-2 rounded-md bg-warm-50 border border-warm-300 text-xs text-ink">
          {error}
        </div>
      )}
      <div className="space-y-2">
        <SubField label="Full name" required>
          <TextInput
            type="text"
            nameCase="person"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className={inputCls()}
            aria-required="true"
          />
        </SubField>
        <SubField label="Title">
          <TextInput
            type="text"
            nameCase="role"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={inputCls()}
            placeholder="Director of Memory Care"
          />
        </SubField>
        <SubField label="Email">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls()}
          />
        </SubField>
        <SubField label="Business phone">
          <input
            type="tel"
            value={businessPhone}
            onChange={(e) => setBusinessPhone(e.target.value)}
            className={inputCls()}
          />
        </SubField>
        <SubField label="Cell phone">
          <input
            type="tel"
            value={cellPhone}
            onChange={(e) => setCellPhone(e.target.value)}
            className={inputCls()}
          />
        </SubField>
        <SubField label="Fax">
          <input
            type="tel"
            value={fax}
            onChange={(e) => setFax(e.target.value)}
            className={inputCls()}
          />
        </SubField>
        <AddressFields idPrefix="inline-contact" {...address.fields} />
        <AddressCheckPrompt phase={address.check.phase} />
        {address.prompting && (
          <div className="flex flex-wrap justify-end gap-2">
            <AddressCheckChoices
              phase={address.check.phase}
              busy={false}
              onKeep={() => address.choose('checked', false)}
              onUse={() => address.choose('checked', true)}
              onSaveAsTyped={() => address.choose('saved_as_typed', false)}
            />
          </div>
        )}
        <div className="flex items-center gap-2">
          <input
            id="inline-ind-ref-partner"
            type="checkbox"
            checked={referralPartner}
            onChange={(e) => setReferralPartner(e.target.checked)}
            className="w-4 h-4"
          />
          <label htmlFor="inline-ind-ref-partner" className="text-sm text-ink">
            Referral partner
          </label>
        </div>
        <SubField label="Notes">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className={inputCls()}
          />
        </SubField>
      </div>
      <div className="flex justify-end gap-2 mt-3">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleSave}>
          Save contact
        </Button>
      </div>
    </div>
  );
}

function SubField({ label, required, children }: { label: string; required?: boolean; children: ReactNode }): ReactNode {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-ink mb-1">
        {label}
        {required && <span className="text-warm-500 ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}

function inputCls(): string {
  // Geometry ruling §12: inputs take rounded-base; the standard sage-400 ring.
  return 'w-full min-h-9 px-3 py-1.5 border border-sage-300 rounded-base text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400';
}

function nullableTrim(v: string): string | null {
  const trimmed = v.trim();
  return trimmed.length === 0 ? null : trimmed;
}
